import React, { useMemo, useState } from 'react';
import { useAbility } from '@casl/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Chip, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Box } from '@mui/system';
import { useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useSearchParams } from 'react-router-dom';

import AutocompleteMultipleElement from '@components/common/HookFormMui/AutocompleteMultipleElement';
import useStaffUsersQuery from '@components/common/hooks/api/useStaffUsersQuery';
import usePearsQuery from '@components/common/hooks/api/usePearsQuery';
import { AbilityContext, Subjects } from '@lib/permissions';
import { getUserLabel, toUserOption } from '@lib/user';
import { parseNumber, truncateString } from '@lib/utils';
import useFormQuery from '@components/common/hooks/api/useFormQuery';
import CheckboxButtonGroup from '@components/common/HookFormMui/CheckboxButtonGroup';

const EventFilters = () => {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const ability = useAbility(AbilityContext);

  const { data: staffUsers, ...staffUsersQuery } = useStaffUsersQuery();
  const { data: pearsData, ...pearsQuery } = usePearsQuery();
  const { data: formQuestions, ...formQuery } = useFormQuery('EVENT');

  const projects = useMemo(
    () => formQuestions?.find((q) => q.slug === 'project')?.question_options?.filter((opt) => opt.active) ?? [],
    [formQuestions]
  );

  const filters = useMemo(() => ({
    project: searchParams.getAll('project').map(parseNumber),
    main_office: searchParams.getAll('main_office'),
    created_by: searchParams.getAll('created_by'),
  }), [searchParams.toString()]);

  const filterForm = useForm({
    mode: 'onSubmit',
    values: filters,
  });

  const {
    handleSubmit,
    control,
    setValue
  } = filterForm;

  const onSubmit = (values) => {
    const input = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      if (!value) {
        return acc;
      }
      const valuesArray = Array.isArray(value) ? value : [value];
      switch (key) {
        case 'project':
        case 'created_by':
        case 'main_office':
          valuesArray.forEach(v => acc.push([key, v.id || v]));
          break;
        default: break;
      }
      return acc;
    }, []);
    setFiltersExpanded(false);
    setSearchParams(input);
  };

  const clearFilters = () => {
    setValue('project', []);
    setValue('created_by', []);
    setValue('main_office', []);
    setSearchParams({});
    // Note: Don't close the filter accordion, per SEMS-1662
  }

  const filterDescriptions = [];
  if (!formQuery.isLoading && filters.project && filters.project.length) {
    const [first, ...rest] = filters.project;
    const label = projects.find(a => a.id === first)?.label;
    filterDescriptions.push(`Grants & Special Projects = ${label} ${rest.length ? `+${rest.length}` : ''}`);
  }
  if (!pearsQuery.isLoading && filters.main_office && filters.main_office.length) {
    const [first, ...rest] = filters.main_office;
    const label = truncateString(first, 50);
    filterDescriptions.push(`Main Office = ${label} ${rest.length ? `+${rest.length}` : ''}`);
  }
  if (!staffUsersQuery.isLoading && filters.created_by && filters.created_by.length) {
    const [first, ...rest] = filters.created_by;
    const user = staffUsers.find(u => filters.created_by.includes(u.email));
    const label = user ? getUserLabel(user) : first;
    filterDescriptions.push(`Created By = ${label} ${rest.length ? `+${rest.length}` : ''}`);
  }

  return (
    <Box sx={{ mt: 2, mb: 2, width: '100%' }}>
      <Accordion
        expanded={filtersExpanded}
        onChange={(ev, isExpanded) => setFiltersExpanded(isExpanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="demographics-content"
          id="demographics-header"

        >
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={4} >
              <Grid xs={12} sm={1}>
                <Typography variant="h3" sx={{ fontSize: '1rem' }}>Filters</Typography>
              </Grid>
              {!filtersExpanded && (
                <Grid xs={12} sm={10} sx={{ padding: 1 }}>
                  <Box>
                    {filterDescriptions.map(f => (
                      <Chip key={f} sx={{margin: '3px'}} label={f} />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormContainer onSuccess={handleSubmit(onSubmit)} FormProps={{ className: 'mui' }}>
            <Box sx={{ mb: 1 }}>
              <CheckboxButtonGroup
                label="Grants & Special Projects"
                id="field-project"
                name="project"
                control={control}
                row
                options={projects}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <AutocompleteMultipleElement
                id="filter-main_office"
                name="main_office"
                label={'Main office'}
                control={control}
                fullWidth
                options={pearsData?.main_offices || []}
              />
            </Box>

            {ability.can('view_all', Subjects.EVENT) && (
              <Box sx={{ mb: 2 }}>
                <AutocompleteMultipleElement
                  id="filter-created_by"
                  name="created_by"
                  label={'Created By'}
                  control={control}
                  fullWidth
                  options={(staffUsers ? staffUsers.map(toUserOption) : [])}
                  autocompleteProps={{
                    isOptionEqualToValue: (option, newValue) => {
                      return option.id === newValue || option.id === newValue.id;
                    }
                  }}
                />
              </Box>
            )}
            <Grid xs={12} sm={10} md={8} sx={{ marginTop: '20px' }}>
              <Button
                id="button-filter-submit"
                type={'submit'}
                variant="contained"
                color="primary"
                size="small"
              >
                Apply Filters
              </Button>
              <Button
                id="button-filter-clear"
                variant="outlined"
                size="small"
                sx={{ marginLeft: '20px' }}
                onClick={clearFilters}
              >
                Clear
              </Button>
            </Grid>
          </FormContainer>
        </AccordionDetails>
      </Accordion>

    </Box>

  );
};

export default EventFilters;
